"use client";

import Link from 'next/link';
import { PawPrint, Menu, Phone, Mail, MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Available Pets', href: '/#available-puppies' },
    { name: 'Community', href: '/community' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Process', href: '/process' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,height,backdrop-filter] duration-300",
      isScrolled ? "bg-background/80 backdrop-blur-md shadow-sm border-b border-border/50 h-16" : "bg-transparent backdrop-blur-sm h-20"
    )}>
      <div className="container mx-auto px-4 flex items-center justify-between h-full">
        {/* Mobile: Left Hamburger / Desktop: Hidden */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
              >
                <Menu className="w-6 h-6 text-foreground" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[350px] p-0 border-r border-border bg-background">
              <div className="flex flex-col h-full bg-background">
                <SheetHeader className="p-6 text-left border-b border-border/50">
                  <SheetTitle className="flex items-center gap-2">
                    <div className="p-2 bg-primary/10 rounded-full text-primary">
                      <PawPrint className="w-5 h-5" />
                    </div>
                    <span className="font-headline text-xl font-black tracking-tight text-foreground">
                      Bay Valley <span className="text-primary italic font-normal">Pups</span>
                    </span>
                  </SheetTitle>
                </SheetHeader>
                
                <div className="flex-1 overflow-y-auto py-6 px-6">
                  <div className="space-y-6">
                    <div className="space-y-1">
                      <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Menu</p>
                      {navLinks.map((link) => (
                        <SheetClose key={link.name} asChild>
                          <Link 
                            href={link.href}
                            className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors"
                          >
                            {link.name}
                          </Link>
                        </SheetClose>
                      ))}
                    </div>

                    <div className="space-y-4 pt-6 border-t border-border/50">
                      <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Focus</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-secondary rounded-full text-xs font-bold text-secondary-foreground">Toy Poodle</span>
                        <span className="px-3 py-1 bg-secondary rounded-full text-xs font-bold text-secondary-foreground">Maltipoo</span>
                        <span className="px-3 py-1 bg-secondary rounded-full text-xs font-bold text-secondary-foreground">Home Raised</span>
                      </div>
                    </div>

                    <div className="space-y-4 pt-6 border-t border-border/50">
                      <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Contact</p>
                      <div className="grid gap-3">
                        <Button asChild variant="outline" className="justify-start gap-3 w-full border-border hover:bg-secondary">
                          <a href="tel:4086386236">
                            <Phone className="w-4 h-4" /> (408) 638-6236
                          </a>
                        </Button>
                        <Button asChild variant="outline" className="justify-start gap-3 w-full border-border hover:bg-secondary">
                          <a href="sms:4086386236">
                            <Mail className="w-4 h-4" /> Text Us
                          </a>
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Serving Bay Area & Roseville
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Center Brand */}
        <Link href="/" className="flex items-center gap-2 group absolute left-1/2 top-1/2 -translate-x-1/2 md:static md:translate-x-0 md:transform-none">
          <span className="font-headline text-xl md:text-2xl font-bold tracking-tight text-foreground whitespace-nowrap">
            Bay Valley Pups
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8 mx-8">
            {navLinks.map((item) => (
              <Link 
                key={item.name}
                href={item.href} 
                className="text-sm font-medium tracking-wide text-muted-foreground hover:text-primary transition-all duration-300"
              >
                {item.name}
              </Link>
            ))}
        </nav>

        {/* CTA (Mobile & Desktop) */}
        <div className="flex items-center gap-4">
            <Button asChild size="sm" className="rounded-full px-6 h-10 font-bold shadow-soft hover:shadow-soft-hover transition-all duration-500 bg-primary hover:bg-primary/90 text-primary-foreground border-none">
                <a href="tel:4086386236">
                  <Phone className="w-4 h-4 mr-2" />
                  <span className="md:hidden">Call Us</span>
                  <span className="hidden md:inline">(408) 638-6236</span>
                </a>
            </Button>
        </div>
      </div>
    </header>
  );
}
